/**
 * WHAT: Visit Counter Service
 * WHY: Track daily visits without PII
 * HOW: localStorage-based daily tracking
 * IMPORTANCE: Privacy-friendly analytics
 */

import { CONFIG } from '../config/config.js';

class VisitCounterService {
    constructor() {
        this.storageKey = 'sourdoughies_visit_date';
        this.apiUrl = CONFIG.getApiUrl('visit-counter');
    }

    /**
     * WHAT: Check if visit should be counted
     * WHY: Only count one visit per day per browser
     * HOW: Check localStorage for today's date
     * IMPORTANCE: Privacy-friendly tracking
     */
    shouldCountVisit() {
        const today = new Date().toDateString();
        const lastVisitDate = localStorage.getItem(this.storageKey);
        
        return lastVisitDate !== today;
    }

    /**
     * WHAT: Mark visit as counted
     * WHY: Prevent multiple counts per day
     * HOW: Store today's date in localStorage
     * IMPORTANCE: Daily visit limit
     */
    markVisitCounted() {
        const today = new Date().toDateString();
        localStorage.setItem(this.storageKey, today);
    }

    /**
     * WHAT: Increment visit counter
     * WHY: Track daily visits
     * HOW: API call to backend
     * IMPORTANCE: Analytics data
     */
    async incrementVisit() {
        if (!this.shouldCountVisit()) {
            return false; // Already counted today
        }

        try {
            const response = await fetch(`${this.apiUrl}/increment`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                this.markVisitCounted();
                return true;
            } else {
                console.warn('Failed to increment visit counter');
                return false;
            }
        } catch (error) {
            console.warn('Error incrementing visit counter:', error);
            return false;
        }
    }

    /**
     * WHAT: Get visit statistics
     * WHY: Display analytics data
     * HOW: API call to backend
     * IMPORTANCE: Analytics dashboard
     */
    async getVisitStats(period = '7d') {
        try {
            const response = await fetch(`${this.apiUrl}/stats?period=${period}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error getting visit stats:', error);
            return null;
        }
    }

    /**
     * WHAT: Get daily visit counts
     * WHY: Show visit trends
     * HOW: API call to backend
     * IMPORTANCE: Analytics visualization
     */
    async getDailyCounts(period = '7d') {
        try {
            const response = await fetch(`${this.apiUrl}/daily?period=${period}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error getting daily counts:', error);
            return null;
        }
    }

    /**
     * WHAT: Get comprehensive visit analytics
     * WHY: Provide all visit data
     * HOW: API call to backend
     * IMPORTANCE: Analytics dashboard
     */
    async getVisitAnalytics(period = '7d') {
        try {
            const response = await fetch(`${this.apiUrl}/analytics?period=${period}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error getting visit analytics:', error);
            return null;
        }
    }
}

// Export singleton instance
export default new VisitCounterService();
